import { clsx } from 'clsx'
import React from 'react'

// Heading Component
export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  size?: '5xl' | '4xl' | '3xl' | '2xl' | 'xl' | 'lg'
  variant?: 'default' | 'gradient' | 'secondary' | 'muted'
  children: React.ReactNode
}

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      as: Component = 'h1',
      size,
      variant = 'default',
      className,
      children,
      ...props
    },
    ref
  ) => {
    // Default size mapping based on heading level
    const defaultSizes = {
      h1: '3xl',
      h2: '2xl',
      h3: 'xl',
      h4: 'lg',
      h5: 'lg',
      h6: 'lg',
    }

    const actualSize = size || defaultSizes[Component]

    const baseClasses = ['font-semibold', 'leading-tight', 'tracking-tight']

    const sizeClasses = {
      '5xl': ['text-5xl', 'lg:text-5xl'],
      '4xl': ['text-4xl', 'lg:text-4xl'],
      '3xl': ['text-3xl', 'md:text-4xl'],
      '2xl': ['text-2xl', 'md:text-3xl'],
      xl: ['text-xl', 'md:text-2xl'],
      lg: ['text-lg', 'md:text-xl'],
    }

    const variantClasses = {
      default: ['text-primary'],
      gradient: ['text-gradient'],
      secondary: ['text-secondary'],
      muted: ['text-muted'],
    }

    const headingClasses = clsx(
      baseClasses,
      sizeClasses[actualSize as keyof typeof sizeClasses],
      variantClasses[variant],
      className
    )

    return (
      <Component ref={ref} className={headingClasses} {...props}>
        {children}
      </Component>
    )
  }
)

Heading.displayName = 'Heading'

// Text Component
export interface TextProps extends React.HTMLAttributes<HTMLParagraphElement> {
  as?: 'p' | 'span' | 'div'
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl'
  variant?: 'primary' | 'secondary' | 'tertiary' | 'muted' | 'accent'
  weight?: 'normal' | 'medium' | 'semibold' | 'bold'
  children: React.ReactNode
}

export const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
  (
    {
      as: Component = 'p',
      size = 'base',
      variant = 'primary',
      weight = 'normal',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = ['leading-relaxed']

    const sizeClasses = {
      xs: ['text-xs'],
      sm: ['text-sm'],
      base: ['text-base'],
      lg: ['text-lg'],
      xl: ['text-xl'],
    }

    const variantClasses = {
      primary: ['text-primary'],
      secondary: ['text-secondary'],
      tertiary: ['text-tertiary'],
      muted: ['text-muted'],
      accent: ['text-gradient'],
    }

    const weightClasses = {
      normal: ['font-normal'],
      medium: ['font-medium'],
      semibold: ['font-semibold'],
      bold: ['font-bold'],
    }

    const textClasses = clsx(
      baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      weightClasses[weight],
      className
    )

    return (
      <Component ref={ref} className={textClasses} {...props}>
        {children}
      </Component>
    )
  }
)

Text.displayName = 'Text'

// Link Component
export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: 'default' | 'accent' | 'muted'
  size?: 'sm' | 'base' | 'lg'
  children: React.ReactNode
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { variant = 'default', size = 'base', className, children, ...props },
    ref
  ) => {
    const baseClasses = [
      'transition-normal',
      'underline-offset-4',
      'hover:underline',
    ]

    const sizeClasses = {
      sm: ['text-sm'],
      base: ['text-base'],
      lg: ['text-lg'],
    }

    const variantClasses = {
      default: ['text-primary', 'hover:text-secondary'],
      accent: ['text-gradient', 'hover:opacity-80'],
      muted: ['text-muted', 'hover:text-secondary'],
    }

    const linkClasses = clsx(
      baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      className
    )

    return (
      <a ref={ref} className={linkClasses} {...props}>
        {children}
      </a>
    )
  }
)

Link.displayName = 'Link'

// Code Component
export interface CodeProps extends React.HTMLAttributes<HTMLElement> {
  as?: 'code' | 'pre'
  variant?: 'inline' | 'block'
  children: React.ReactNode
}

export const Code = React.forwardRef<HTMLElement, CodeProps>(
  (
    {
      as: Component = 'code',
      variant = 'inline',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = ['font-mono', 'text-sm']

    const variantClasses = {
      inline: ['bg-surface', 'text-primary', 'px-1', 'py-0.5', 'rounded'],
      block: [
        'bg-surface',
        'text-primary',
        'p-4',
        'rounded-lg',
        'block',
        'overflow-x-auto',
      ],
    }

    const codeClasses = clsx(baseClasses, variantClasses[variant], className)

    return (
      <Component ref={ref as any} className={codeClasses} {...props}>
        {children}
      </Component>
    )
  }
)

Code.displayName = 'Code'
